/**
 * Guide contextual-CTA copy registry.
 *
 * Guides carry ~60% of organic landing traffic but almost no guide reader
 * reaches /pricing or /signup (measured 2026-08-24: 2 signups, 0 customers in
 * 90 days). The tracked in-content ask (<GuideStartCta>, above the TOC) fixes
 * the placement problem; this registry fixes the copy problem: the ask now
 * names the document the reader is reading about instead of the generic
 * "Create your first job pack free" headline.
 *
 * COPY RULES — these are enforced by __tests__/publishing-claims-guard.test.ts
 * (a failed sentence names its file and class) and reflected in
 * lib/guides/__tests__/cta-copy.test.ts:
 *
 *   1. No speed figures. A duration quantity or immediacy promise is a time
 *      claim with no admissible evidence (see E.GENERATION: configuration
 *      timeouts cannot approve a customer-facing generation latency). The
 *      adopted rewording drops the figure entirely.
 *   2. No compliance outcomes. The full compliance-outcome family (the
 *      banned adjective beside a document noun, requirement verbs with
 *      CDM/HSE objects, obligation-coverage verbs) is out; the adopted model
 *      is a first draft structured around CDM 2015 for competent review.
 *   3. No superiority framing (superlatives, "-er than", "first and only").
 *   4. The intro/end card body keeps the free-account offer, so headlines may
 *      only promise Starter-covered outputs (RAMS, construction phase plan,
 *      site induction, the one-job pack) — never a Pro-gated feature (F10,
 *      COSHH, toolbox talks, permits, incident log) without naming the tier.
 *      Pro-topic guides angle the ask at the Starter-covered documents the
 *      same reader needs.
 *   5. Product-fact truth: The Site Book does NOT file the F10 with the HSE
 *      (content/help/f10-notification.ts — it pre-fills the data for the
 *      HSE's online form and records when you have notified), and there is no
 *      standalone "welfare plan" document type (welfare arrangements are a
 *      CPP section). Headlines reflect what the product actually does.
 *
 *   6. Business-intent guides (`intent: "business-walkthrough"`, conversion programme
 *      31 Oct 2026) swap the free ask for the Business walkthrough. Their
 *      body describes what Business adds and names Business, so a team or
 *      site feature never reads as part of the free offer; the free account
 *      survives only as the card's small secondary link.
 *
 * A registration IS the approval: nothing here should need a CLAIMS_REGISTER
 * entry — if a sentence trips the class pre-filter, reword it rather than
 * registering it.
 */
export interface GuideCtaCopy {
  /** Topic-specific headline shown on the intro (above-TOC) and end CTAs. */
  headline: string;
  /** Supporting line under the headline. Falls back to the shared default. */
  body?: string;
  /**
   * `"business-walkthrough"` routes the ask to the Business walkthrough
   * (/business#book-walkthrough) instead of the free signup, with the free
   * account kept as a small secondary link. Only for guides whose reader is a
   * principal contractor or builder-run firm managing other people on site
   * (conversion programme, 31 Oct 2026). Omitted = the free ask.
   */
  intent?: "business-walkthrough";
}

/** Shared fallback body — the proven Starter-scope free-account offer. */
export const GUIDE_CTA_DEFAULT_BODY =
  "Set up a free account and generate the RAMS, construction phase plan and site induction for one real job. No card needed.";

const cta = (headline: string, body?: string): GuideCtaCopy => ({ headline, body });

/**
 * A Business-intent ask. The body must describe what Business adds (team
 * logins, worker inductions, attendance, site files) — never the free-account
 * offer, which the card keeps only as its secondary link.
 */
const businessCta = (headline: string, body: string): GuideCtaCopy => ({
  headline,
  body,
  intent: "business-walkthrough",
});

/**
 * Default guide CTA destination: the value-first /start-project job-pack
 * funnel (conversion programme 2026-10-31), not the bare /signup form. The
 * attribution shape matches the blog article CTA: `utm_content` names the
 * surface family and `utm_term` carries the guide slug (a first-class
 * attribution field, lib/attribution.ts). The name is kept so the existing
 * call sites stay untouched.
 */
export function guideSignupHref(slug: string): string {
  return `/start-project?utm_offer=free_first_job&utm_content=guide_cta&utm_term=${encodeURIComponent(slug)}`;
}

/**
 * Topic CTA copy per guide slug. Every guide in the registry (lib/guides)
 * MUST have an entry — lib/guides/__tests__/cta-copy.test.ts fails a new
 * guide without one, mirroring the Phase-60 coverage guards.
 */
export const GUIDE_CTA_COPY: Record<string, GuideCtaCopy> = {
  // ── CDM / CPP cluster ────────────────────────────────────────────────
  "what-is-cdm": cta("Every project needs a CPP — write yours for the actual job"),
  "what-is-a-cpp": cta("Draft your construction phase plan from your project details"),
  "construction-phase-plan-example": cta(
    "Skip the template. Generate your Construction Phase Plan for the actual job",
  ),
  "do-i-need-cpp-domestic-extension": cta(
    "Domestic extension? The CPP duty still applies — write yours",
  ),
  "cdm-2015-small-builders": cta(
    "Small builder, big duties — generate the CPP and RAMS for the job",
  ),
  "domestic-client-cdm": cta(
    "Domestic or commercial — every project needs a CPP before work starts",
  ),
  // Business intent: the reader manages a site, a team and subcontractors.
  "principal-contractor-duties": businessCta(
    "Running jobs as principal contractor? See Business on a real job",
    "Business gives your office and site team shared logins, worker inductions signed on the phone, attendance and site files around each job's RAMS and CPP.",
  ),
  "health-and-safety-file-construction": businessCta(
    "Keep the job's files where your team and subcontractors can find them",
    "Business adds site files, team logins and portal access for assigned workers and subcontractors, so the documents that feed the health and safety file stay with the job.",
  ),
  "thesitebook-vs-template-net-cpp": cta(
    "A CPP written for your job — not a downloaded template",
  ),
  "what-paperwork-for-construction-job": cta(
    "Generate the job's RAMS, CPP and induction in one place",
  ),
  "paperwork-for-kitchen-fit": cta(
    "Kitchen fit? Generate the RAMS and CPP for the actual job",
  ),

  // ── RAMS / risk assessment cluster ───────────────────────────────────
  "what-is-rams": cta("Turn your job details into a RAMS you can brief from"),
  "rams-example": cta("Turn this example into your own RAMS"),
  "how-to-write-rams-construction": cta(
    "Write the RAMS without staring at a blank page",
  ),
  "risk-assessment-construction": cta(
    "Generate a risk assessment from your job's actual hazards",
  ),
  "method-statement-guide": cta(
    "Write the method statement alongside the risk assessment",
  ),
  "rams-for-house-extension": cta(
    "Generate the RAMS for your extension — hazards, controls, method",
  ),
  "rams-for-loft-conversion": cta(
    "Generate the RAMS for your loft conversion — hazards, controls, method",
  ),
  "rams-for-roof-work": cta(
    "Generate the RAMS for your roof job — hazards, controls, method",
  ),
  "rams-rejected-by-building-control": cta(
    "Rejected RAMS? Rewrite it with job-specific detail",
  ),
  "review-subcontractor-rams": businessCta(
    "Checking subcontractor RAMS on every job? See how Business keeps it together",
    "Business adds team logins, subcontractor evidence records, site files and attendance around the job, so the office and site team work from one record.",
  ),
  "what-happens-after-rams-generation": cta(
    "Brief it, sign it, file it — run your RAMS end to end",
  ),

  // ── Hazard / activity cluster ────────────────────────────────────────
  "excavation-safety": cta(
    "Need an excavation RAMS or method statement? Generate it from your job details",
  ),
  "working-in-cold-weather-construction": cta(
    "Add your winter controls to the RAMS and site induction",
  ),
  "construction-site-inspection-record": cta(
    "Turn your walkaround checks into the job's written RAMS and CPP",
  ),
    "excavation-inspection-checklist": cta(
    "Turn your excavation inspection regime into written RAMS",
  ),
  "scaffolding-safety": cta("Create a scaffolding safety RAMS for your job"),
  "working-at-height": cta("Create a working-at-height RAMS for your job"),
  "electrical-safety-construction": cta(
    "Write the RAMS that covers your electrical work controls",
  ),
  "fire-safety-construction": cta(
    "Write the fire risks and controls into your site paperwork",
  ),
  "manual-handling-construction": cta(
    "Write the manual handling controls into your RAMS",
  ),
  "noise-vibration-construction": cta(
    "Write the noise and vibration controls into your RAMS",
  ),
  "dust-silica-construction": cta(
    "Write the dust and silica controls into your RAMS",
  ),
  "confined-spaces": cta("Write a confined space RAMS from your method"),
  "hot-works-permit": cta("Write the RAMS behind every hot works permit"),
  "ppe-construction": cta("Specify the actual PPE in your job's RAMS"),
  "slips-trips-falls": cta("Write the housekeeping risks into your RAMS"),
  "lone-working-construction": cta(
    "Write the lone working arrangements into your RAMS",
  ),
  "asbestos-awareness-builders": cta(
    "Write asbestos RAMS that reference the survey",
  ),
  // COSHH itself is Pro-gated, so these asks angle at the Starter-covered
  // site documents the same reader needs (rule 4 in the header).
  "coshh-for-builders": cta(
    "Write the RAMS for the substances your job involves",
  ),
  "coshh-assessment-example-construction": cta(
    "Use this example to shape your own site paperwork",
  ),
  "most-common-construction-site-hazards": cta(
    "Generate RAMS that name your site's actual hazards",
  ),
  "temporary-works": cta("Brief every temporary works method in writing"),

  // ── Site management / people cluster ─────────────────────────────────
  "how-to-do-site-induction": cta(
    "Run the induction, then collect the signature digitally",
  ),
  "how-to-run-toolbox-talk": cta(
    "Create the site pack your toolbox talks point to",
  ),
  "incident-reporting-riddor": cta(
    "Create the site paperwork pack — RAMS, CPP, induction",
  ),
  "hse-inspection": cta(
    "Keep the site paperwork an inspector asks to see",
  ),
  "what-happens-hse-site-inspection": cta(
    "Keep the site paperwork an inspector asks to see",
  ),
  "welfare-facilities-construction": cta(
    "Put your welfare arrangements in writing as part of your CPP",
  ),
  "f10-notification": cta(
    "Notifiable project? You'll need a Construction Phase Plan too",
  ),
  "building-control-for-extensions": cta(
    "Alongside building control: write the job's RAMS and CPP",
  ),
  "what-documents-do-subcontractors-need": cta(
    "Issue the site rules, RAMS and induction from one place",
  ),
  "ssip-accreditation-small-builders": cta(
    "Build the RAMS and site induction records an SSIP assessor asks to see",
  ),
  "storms-high-winds-construction": cta(
    "Put your wind limits and storm checks in the job's RAMS",
  ),
  "building-safety-levy-small-builders": cta(
    "The levy is the client's bill: keep your own RAMS and CPP in order",
  ),
  "removing-load-bearing-wall": cta(
    "Build the wall removal RAMS around the engineer's design",
  ),
  "first-aid-construction-site": cta(
    "Put your first-aid arrangements in the job's RAMS and induction",
  ),
  "building-regulations-dutyholders-competence": cta(
    "Building Regulations duties sit beside CDM: write the job's RAMS and CPP too",
  ),
  "what-insurance-do-builders-need": cta(
    "Insurance sorted — now the site paperwork",
  ),
  "mental-health-construction": cta(
    "Write welfare arrangements that cover the whole site",
  ),
  "scheduling-financial-management": cta(
    "Schedule the job, then write its paperwork in one place",
  ),
  "cscs-smart-check": cta(
    "Card checked? Now write the site induction they'll sign",
  ),
  "best-cis-software-for-builders": cta(
    "CIS handles the tax — handle the site paperwork here",
  ),
  "best-tools-builders-paperwork-2026": cta(
    "One place for the paperwork this list skips",
  ),
  "how-to-set-up-the-site-book-customise-your-account-in-under-10-minutes":
    cta("Set up your account and create your first job pack"),
};

/** Resolve topic CTA copy for a guide slug, with the shared default. */
export function getGuideCtaCopy(slug: string): GuideCtaCopy {
  const entry = GUIDE_CTA_COPY[slug];
  return {
    headline: entry?.headline ?? "Create your first job pack free",
    body: entry?.body ?? GUIDE_CTA_DEFAULT_BODY,
    ...(entry?.intent ? { intent: entry.intent } : {}),
  };
}

/** True when a guide's in-content ask routes to the Business walkthrough. */
export function isBusinessIntentGuide(slug: string): boolean {
  return GUIDE_CTA_COPY[slug]?.intent === "business-walkthrough";
}

/** Slugs that display a worked example document and get the post-example ask. */
export const GUIDES_WITH_EXAMPLE_BLOCKS: readonly string[] = [
  "construction-phase-plan-example",
  "rams-example",
  "coshh-assessment-example-construction",
];
